import { inject } from 'vue';

import { cloneDeep } from 'lodash';

import { defineStore } from 'pinia';
import { ChangeAppointmentTimesCommand } from 'src/stores/appointment/commands/ChangeAppointmentTimesCommand';
import { CreateAppointmentTimerCommand } from 'src/stores/appointment/commands/CreateAppointmentTimerCommand';
import { CreateNewAppointmentFromTimestampAndDuration } from 'src/stores/appointment/commands/CreateNewAppointmentFromTimestampAndDuration';
import { CreateNewAppointmentWithTheMinimalRequiredParameters } from 'src/stores/appointment/commands/CreateNewAppointmentWithTheMinimalRequiredParameters';
import { DeleteAppointmentTimerFavoriteCommand } from 'src/stores/appointment/commands/DeleteAppointmentTimerFavoriteCommand';
import { DeleteAppointmentWithMembersCommand } from 'src/stores/appointment/commands/DeleteAppointmentWithMembersCommand';
import { DeleteRepetitionAppointmentWithMembers } from 'src/stores/appointment/commands/DeleteRepetitionAppointmentWithMembers';
import { EditExistingAppointmentCommand } from 'src/stores/appointment/commands/EditExistingAppointmentCommand';
import { EditSingleAppointmentOfRepetitionAppointments } from 'src/stores/appointment/commands/EditSingleAppointmentOfRepetitionAppointmentsCommand';
import { SaveAppointmentTimerFavoritesCommand } from 'src/stores/appointment/commands/SaveAppointmentTimerFavoritesCommand';
import {
	AppointmentQuery,
	ChangeAppointmentTypeOfSubmittedAppointmentId,
	GetAppointmentTypesWithColorQuery,
	GetRunningAppointmentTimerQuery,
	GetTimerFavoritesQuery,
} from 'src/stores/appointment/queries';
import { useUserStore } from 'src/stores/user';

import {
	Appointment,
	AppointmentRunType,
	AppointmentTimerRunType,
	AppointmentType,
	AppointmentTypeRunType,
	AppointmentTypeWithColor,
	AppointmentTypeWithColorRunType,
	TimerFavoriteRunType,
} from 'src/models/appointment';
import { CommandProcessIdHandling } from 'src/models/command';
import { ModuleNameEnum } from 'src/models/module/enums';
import { SetTheStateForMultipleAggregates } from 'src/models/status/SetStatusCommand';
import { StatusEnum } from 'src/models/status/enums';
import { Timer, TimerFavorite } from 'src/models/timer';

import { AggregateComparator } from 'src/util/aggregate/AggregateComparator';
import { AggregateMerger } from 'src/util/aggregate/AggregateMerger';
import {
	aggregateIdWithUnsetSourceAndTargetContainerId,
	buildAggregateId,
} from 'src/util/aggregate/AggregateUtilityFunctions';
import { CommandBus } from 'src/util/command/CommandBus';
import { releaseRecord, retainRecord } from 'src/util/records/recordLeases';

import {
	AppointmentCallback,
	ChangeAppointmentTypeOfSubmittedAppointmentIdCallBack,
	GetAppointmentTypesWithColorCallBack,
	GetRunningAppointmentTimerCallBack,
	GetTimerFavoritesCallBack,
} from './callbacks';
import {
	AddRunningAppointmentTimerPayload,
	AddTimerFavoritePayload,
	AggregateIdPayload,
	AppointmentStoreState,
	ChangeAppointmentTimesPayload,
	CreateNewAppointmentWithTheMinimalRequiredParametersPayload,
	CreateTheAppointmentPayload,
	DeleteTheAppointmentPayload,
	DeleteTimerFavoritePayload,
	FetchAppointmentPayload,
	MarkAppointmentTimesAsSavedPayload,
	QueryStorePayload,
	ReleaseAppointmentPayload,
	RemoveTimerFavoritePayload,
	ResetPayload,
	RetainAppointmentPayload,
	SaveTheAppointmentPayload,
	SaveTheAppointmentTimerFavoritesPayload,
	SaveTheAppointmentTimerPayload,
	SetAppointmentTypesWithColorPayload,
	SetRunningAppointmentTimerPayload,
	SetTheAppointmentStatePayload,
	SetTheAppointmentTypePayload,
	SetTimerFavoritesPayload,
	UnsubscribeFetchAppointmentPayload,
	UpdateRunningAppointmentTimerTitlePayload,
	UpdateTheAppointmentAccessPayload,
	UpdateTheAppointmentAlertPayload,
	UpdateTheAppointmentDescriptionPayload,
	UpdateTheAppointmentEndDateAndTimePayload,
	UpdateTheAppointmentIdoButtonPayload,
	UpdateTheAppointmentLocationPayload,
	UpdateTheAppointmentParticipantPayload,
	UpdateTheAppointmentParticipantsPayload,
	UpdateTheAppointmentRepetitionPayload,
	UpdateTheAppointmentStartDateAndTimePayload,
	UpdateTheAppointmentTimeModePayload,
	UpdateTheAppointmentTitlePayload,
	UpdateTheAppointmentTypePayload,
} from './interfaces';

const normalizeAggregateId = (aggregateId: string): string =>
	aggregateIdWithUnsetSourceAndTargetContainerId(aggregateId);

const createDefaultState = (): AppointmentStoreState => ({
	appointmentMutationCounter: 0,
	appointmentTimer: [],
	appointmentTypeWithColor: [],
	appointments: new Map<string, Appointment>(),
	initialLoadedAppointments: new Map<string, Appointment>(),
	newAppointmentAggregateId: '',
	newAppointmentPrefersCalendarSplitView: false,
	newTimerAppointmentAggregateId: '',
	timerFavorites: [],
});

const mergeAppointmentWithFallback = (
	initialLoadedAppointment: Appointment,
	currentAppointment: Appointment,
	incomingAppointment: Appointment
): Appointment => {
	try {
		return AggregateMerger.merge(initialLoadedAppointment, currentAppointment, incomingAppointment) as Appointment;
	} catch (error) {
		console.error('Failed to merge appointment aggregate. Falling back to the latest server payload.', error);
		return cloneDeep<Appointment>(incomingAppointment);
	}
};

const extractAggregateIdFromCommandResult = (result: unknown): string => {
	const results = (
		result as {
			data?: {
				data?: {
					results?: {
						aggregate?: {
							aggregateId?: unknown;
						};
						aggregateId?: unknown;
						appointmentId?: unknown;
					};
				};
			};
		}
	).data?.data?.results;
	const nestedAggregateId = results?.aggregate?.aggregateId;
	if (typeof nestedAggregateId === 'string' && nestedAggregateId !== '') {
		return nestedAggregateId;
	}

	const directAggregateId = results?.aggregateId;
	if (typeof directAggregateId === 'string' && directAggregateId !== '') {
		return directAggregateId;
	}

	const appointmentId = results?.appointmentId;
	const authenticatedUserId = useUserStore().authenticatedUserInformation?.id;
	if (
		typeof appointmentId !== 'number' ||
		!Number.isInteger(appointmentId) ||
		appointmentId <= 0 ||
		typeof authenticatedUserId !== 'number' ||
		!Number.isInteger(authenticatedUserId) ||
		authenticatedUserId <= 0
	) {
		return '';
	}

	return buildAggregateId({
		entryId: appointmentId,
		moduleName: ModuleNameEnum.MODULE_APPOINTMENT,
		userId: authenticatedUserId,
	});
};

const resolveStateCommandPayload = (
	payload: SetTheAppointmentStatePayload | string
): { aggregateId: string; commandBus: CommandBus | undefined } => {
	if (typeof payload === 'string') {
		return {
			aggregateId: payload,
			commandBus: inject<CommandBus>('commandBus'),
		};
	}

	return {
		aggregateId: payload.aggregateId,
		commandBus: payload.commandBus ?? inject<CommandBus>('commandBus'),
	};
};

const parseAggregateEntryId = (aggregateId: string): number => {
	return (
		JSON.parse(aggregateId) as {
			entryId: number;
		}
	).entryId;
};

const toLocalISOStringWithoutOffset = (date: Date): string => {
	const pad = (value: number) => String(value).padStart(2, '0');

	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
		date.getDate()
	)}T${pad(date.getHours())}:${pad(date.getMinutes())}:00.000Z`;
};

const toUtcStringWithoutOffset = (date: Date): string => {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	const hours = String(date.getHours()).padStart(2, '0');
	const minutes = String(date.getMinutes()).padStart(2, '0');
	const seconds = String(date.getSeconds()).padStart(2, '0');
	const milliseconds = String(date.getMilliseconds()).padStart(3, '0');

	return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}Z`;
};

export const useAppointmentStore = defineStore('appointment', {
	actions: {
		activateTheAppointmentParticipantAlertFlag(payload: AggregateIdPayload): void {
			const appointmentFromCollection = this.appointments.get(normalizeAggregateId(payload.aggregateId));
			if (appointmentFromCollection !== undefined) {
				appointmentFromCollection.participantsOfAppointment =
					appointmentFromCollection.participantsOfAppointment.map((participant) => ({
						...participant,
						alert: 'true',
					}));
				this.appointments.set(
					normalizeAggregateId(appointmentFromCollection.aggregateId),
					appointmentFromCollection
				);
			}
		},
		addRunningAppointmentTimer(payload: AddRunningAppointmentTimerPayload): void {
			this.appointmentTimer.push(payload.timer);
		},
		addTimerFavorite(payload: AddTimerFavoritePayload): void {
			const exists = this.timerFavorites.some(
				(favorite) =>
					favorite.appointmentTypeId === payload.favorite.appointmentTypeId &&
					favorite.appointmentRoundingMinutes === payload.favorite.appointmentRoundingMinutes &&
					favorite.title === payload.favorite.title
			);
			if (!exists) {
				this.timerFavorites.push(payload.favorite);
			}
		},
		appointment(aggregateId: string): Appointment | undefined {
			return this.appointments.get(aggregateId);
		},
		changeAppointmentTimes(payload: ChangeAppointmentTimesPayload): void {
			const aggregateId = String(payload.appointment.aggregateId ?? '');
			if (aggregateId === '') {
				return;
			}

			void payload.commandBus
				?.sendCommand(
					new ChangeAppointmentTimesCommand(payload.appointment, payload.startDate, payload.endDate)
				)
				?.then(() => {
					this.appointmentMutationCounter += 1;
				});

			const startDateAndTime = toLocalISOStringWithoutOffset(payload.startDate);
			const endDateAndTime = toLocalISOStringWithoutOffset(payload.endDate);
			this.updateTheAppointmentStartDateAndTime({
				aggregateId: aggregateId,
				startDateAndTime: startDateAndTime,
			});
			this.updateTheAppointmentEndDateAndTime({
				aggregateId: aggregateId,
				endDateAndTime: endDateAndTime,
			});
			this.markAppointmentTimesAsSaved({
				aggregateId: aggregateId,
				endDate: endDateAndTime,
				startDate: startDateAndTime,
			});
		},
		createNewAppointmentWithTheMinimalRequiredParameters(
			payload: CreateNewAppointmentWithTheMinimalRequiredParametersPayload
		): void {
			void payload.commandBus
				?.sendCommand(
					new CreateNewAppointmentWithTheMinimalRequiredParameters(
						payload.authenticatedUserId,
						payload.startTimestamp,
						payload.duration
					)
				)
				?.then((result) => {
					this.newAppointmentPrefersCalendarSplitView = true;
					this.setNewAppointmentAggregateId(extractAggregateIdFromCommandResult(result));
				});
		},
		createNewTimerAppointmentWithTheMinimalRequiredParameters(
			payload: CreateNewAppointmentWithTheMinimalRequiredParametersPayload
		): void {
			void payload.commandBus
				?.sendCommand(
					new CreateNewAppointmentWithTheMinimalRequiredParameters(
						payload.authenticatedUserId,
						payload.startTimestamp,
						payload.duration
					)
				)
				?.then((result) => {
					this.setNewTimerAppointmentAggregateId(extractAggregateIdFromCommandResult(result));
				});
		},
		createTheAppointment(payload: CreateTheAppointmentPayload): void {
			void payload.commandBus?.sendCommand(new CreateNewAppointmentFromTimestampAndDuration())?.then((result) => {
				this.newAppointmentPrefersCalendarSplitView = false;
				this.setNewAppointmentAggregateId(extractAggregateIdFromCommandResult(result));
			});
		},
		deactivateTheAppointmentParticipantAlertFlag(payload: AggregateIdPayload): void {
			const appointmentFromCollection = this.appointments.get(normalizeAggregateId(payload.aggregateId));
			if (appointmentFromCollection !== undefined) {
				appointmentFromCollection.participantsOfAppointment =
					appointmentFromCollection.participantsOfAppointment.map((participant) => ({
						...participant,
						alert: 'false',
					}));
				this.appointments.set(
					normalizeAggregateId(appointmentFromCollection.aggregateId),
					appointmentFromCollection
				);
			}
		},
		deleteTheAppointment(payload: DeleteTheAppointmentPayload): void {
			if (payload.queryStore !== undefined) {
				this.unsubscribeFetchAppointment({
					aggregateId: payload.aggregateId,
					queryStore: payload.queryStore,
				});
			}

			if (payload.deleteScope === 'all') {
				void payload.commandBus
					?.sendCommand(
						new DeleteRepetitionAppointmentWithMembers(parseAggregateEntryId(payload.aggregateId))
					)
					?.then(() => {
						this.appointmentMutationCounter += 1;
					});
				return;
			}

			void payload.commandBus
				?.sendCommand(new DeleteAppointmentWithMembersCommand(parseAggregateEntryId(payload.aggregateId)))
				?.then(() => {
					this.appointmentMutationCounter += 1;
				});
		},
		deleteTimerFavorite(payload: DeleteTimerFavoritePayload): void {
			void payload.commandBus?.sendCommand(new DeleteAppointmentTimerFavoriteCommand(payload.id));
		},
		discardChanges(payload: AggregateIdPayload): void {
			const normalizedAggregateId = normalizeAggregateId(payload.aggregateId);
			const baseline = this.initialLoadedAppointments.get(normalizedAggregateId);
			if (baseline === undefined) {
				return;
			}

			this.appointments.set(normalizedAggregateId, cloneDeep<Appointment>(baseline));
		},
		fetchAppointment(payload: FetchAppointmentPayload): Promise<void> {
			if (payload.queryStore === undefined) {
				this.reset({
					aggregateId: payload.aggregateId,
				});
				return Promise.resolve();
			}

			this.unsubscribeFetchAppointment({
				aggregateId: payload.aggregateId,
				queryStore: payload.queryStore,
			});

			return payload.queryStore.query<Appointment>({
				callback: new AppointmentCallback((appointment) => this.setAppointment(appointment)),
				createTime: new Date(),
				query: new AppointmentQuery(payload.aggregateId),
				responseType: 'json',
				runType: AppointmentRunType,
			});
		},
		fetchAppointmentTypesWithColor(payload: QueryStorePayload): void {
			if (payload.queryStore === undefined) {
				this.setAppointmentTypesWithColor({
					appointmentTypeWithColors: [],
				});
				return;
			}

			void payload.queryStore.query<Array<AppointmentTypeWithColor>>({
				callback: new GetAppointmentTypesWithColorCallBack((appointmentTypesWithColor) =>
					this.setAppointmentTypesWithColor(appointmentTypesWithColor)
				),
				createTime: new Date(),
				query: new GetAppointmentTypesWithColorQuery(),
				responseType: 'json',
				runType: AppointmentTypeWithColorRunType,
			});
		},
		fetchRunningAppointmentTimer(payload: QueryStorePayload): void {
			if (payload.queryStore === undefined) {
				this.setRunningAppointmentTimer({
					appointmentTimer: [],
				});
				return;
			}

			void payload.queryStore.query<Array<Timer>>({
				callback: new GetRunningAppointmentTimerCallBack((appointmentTimer) =>
					this.setRunningAppointmentTimer(appointmentTimer)
				),
				createTime: new Date(),
				query: new GetRunningAppointmentTimerQuery(),
				responseType: 'json',
				runType: AppointmentTimerRunType,
			});
		},
		fetchTimerFavorites(payload: QueryStorePayload): void {
			if (payload.queryStore === undefined) {
				this.setTimerFavorites({
					timerFavorites: [],
				});
				return;
			}

			void payload.queryStore.query<Array<TimerFavorite>>({
				callback: new GetTimerFavoritesCallBack((timerFavorites) => this.setTimerFavorites(timerFavorites)),
				createTime: new Date(),
				query: new GetTimerFavoritesQuery(),
				responseType: 'json',
				runType: TimerFavoriteRunType,
			});
		},
		initialLoadedAppointment(aggregateId: string): Appointment | undefined {
			return this.initialLoadedAppointments.get(normalizeAggregateId(aggregateId));
		},
		markAppointmentAsSaved(aggregateId: string): void {
			const normalizedAggregateId = normalizeAggregateId(aggregateId);
			const appointmentFromCollection = this.appointments.get(normalizedAggregateId);
			if (appointmentFromCollection !== undefined) {
				this.initialLoadedAppointments.set(
					normalizedAggregateId,
					cloneDeep<Appointment>(appointmentFromCollection)
				);
			}
		},
		markAppointmentTimesAsSaved(payload: MarkAppointmentTimesAsSavedPayload): void {
			const normalizedAggregateId = normalizeAggregateId(payload.aggregateId);
			const baseline = this.initialLoadedAppointments.get(normalizedAggregateId);
			if (baseline === undefined) {
				return;
			}
			this.initialLoadedAppointments.set(normalizedAggregateId, {
				...cloneDeep<Appointment>(baseline),
				endDate: payload.endDate,
				startDate: payload.startDate,
			});
		},
		release(payload: ReleaseAppointmentPayload): void {
			const normalizedAggregateId = normalizeAggregateId(payload.aggregateId);
			const wasTheLastLease = releaseRecord(`appointment:${normalizedAggregateId}`);
			if (!wasTheLastLease || payload.shouldKeepData) {
				return;
			}

			this.appointments.delete(normalizedAggregateId);
			this.initialLoadedAppointments.delete(normalizedAggregateId);
			this.unsubscribeFetchAppointment({
				aggregateId: payload.aggregateId,
				queryStore: payload.queryStore,
			});
		},
		removeRunningAppointmentTimer(payload: AggregateIdPayload): void {
			const timerIndex = this.appointmentTimer.findIndex((item) => item.aggregateId === payload.aggregateId);
			if (timerIndex !== -1) {
				this.appointmentTimer.splice(timerIndex, 1);
			}
		},
		removeTimerFavorite(payload: RemoveTimerFavoritePayload): void {
			const index = this.timerFavorites.findIndex(
				(favorite) =>
					favorite.appointmentTypeId === payload.appointmentTypeId &&
					favorite.appointmentRoundingMinutes === payload.appointmentStep &&
					favorite.title === payload.title
			);
			if (index !== -1) {
				this.timerFavorites.splice(index, 1);
			}
		},
		reset(payload: ResetPayload): void {
			const normalizedAggregateId = normalizeAggregateId(payload.aggregateId);
			this.appointments.delete(normalizedAggregateId);
			this.initialLoadedAppointments.delete(normalizedAggregateId);
			this.newAppointmentAggregateId = '';
		},
		retain(payload: RetainAppointmentPayload): void {
			retainRecord(`appointment:${normalizeAggregateId(payload.aggregateId)}`);
		},
		runningAppointmentTimer(): Array<Timer> {
			return this.appointmentTimer;
		},
		async saveTheAppointment(payload: SaveTheAppointmentPayload): Promise<boolean> {
			const appointmentFromCollection = this.appointments.get(normalizeAggregateId(payload.aggregateId));

			if (appointmentFromCollection === undefined) {
				return false;
			}

			const command =
				payload.editScope === 'all'
					? new EditExistingAppointmentCommand(appointmentFromCollection)
					: new EditSingleAppointmentOfRepetitionAppointments(appointmentFromCollection);

			const response = await payload.commandBus?.sendCommand(command);

			if (response === null || response === undefined) {
				return false;
			}

			this.appointmentMutationCounter += 1;

			return true;
		},
		saveTheAppointmentTimer(payload: SaveTheAppointmentTimerPayload): void {
			if (payload.timer === undefined) {
				return;
			}

			void payload.commandBus?.sendCommand(
				new CreateAppointmentTimerCommand(
					payload.timer.aggregateId,
					payload.timer.ifsAppointmentId,
					payload.timer.appointmentTypeId,
					toUtcStringWithoutOffset(payload.timer.startTime),
					payload.timer.endTime ? toUtcStringWithoutOffset(payload.timer.endTime) : null,
					payload.timer.title,
					payload.timer.appointmentRoundingMinutes
				)
			);
		},
		saveTheAppointmentTimerFavorites(
			payload: SaveTheAppointmentTimerFavoritesPayload
		): Promise<unknown> | undefined {
			return payload.commandBus?.sendCommand(
				new SaveAppointmentTimerFavoritesCommand(
					payload.favorite.id,
					payload.favorite.appointmentRoundingMinutes,
					payload.favorite.appointmentTypeId,
					payload.favorite.order,
					payload.favorite.title
				)
			);
		},
		setAppointment(payload: Appointment | null): void {
			if (payload === null) {
				return;
			}

			const normalizedAggregateId = normalizeAggregateId(payload.aggregateId);
			const appointmentFromCollection = this.appointments.get(normalizedAggregateId);
			const initialLoadedAppointmentFromCollection = this.initialLoadedAppointments.get(normalizedAggregateId);

			if (
				appointmentFromCollection !== undefined &&
				!AggregateComparator.isEqual(appointmentFromCollection, payload)
			) {
				return;
			}

			if (appointmentFromCollection !== undefined && initialLoadedAppointmentFromCollection !== undefined) {
				const mergedAppointment = mergeAppointmentWithFallback(
					initialLoadedAppointmentFromCollection,
					appointmentFromCollection,
					payload
				);

				this.appointments.set(
					normalizeAggregateId(mergedAppointment.aggregateId),
					cloneDeep<Appointment>(mergedAppointment)
				);
			} else {
				this.appointments.set(normalizedAggregateId, payload);
			}

			this.initialLoadedAppointments.set(normalizedAggregateId, cloneDeep<Appointment>(payload));
		},
		setAppointmentTypesWithColor(payload: SetAppointmentTypesWithColorPayload): void {
			this.appointmentTypeWithColor = payload.appointmentTypeWithColors;
		},
		setNewAppointmentAggregateId(payload: string): void {
			this.newAppointmentAggregateId = payload || '';
		},
		setNewTimerAppointmentAggregateId(payload: string): void {
			this.newTimerAppointmentAggregateId = payload || '';
		},
		setRunningAppointmentTimer(payload: SetRunningAppointmentTimerPayload): void {
			this.appointmentTimer = payload.appointmentTimer;
		},
		setTheAppointmentStateToReady(payload: SetTheAppointmentStatePayload | string): void {
			const resolvedPayload = resolveStateCommandPayload(payload);
			void resolvedPayload.commandBus?.sendCommand(
				new SetTheStateForMultipleAggregates(
					[resolvedPayload.aggregateId],
					CommandProcessIdHandling.IGNORING,
					StatusEnum.READY
				)
			);
		},
		setTheAppointmentStateToSeen(payload: SetTheAppointmentStatePayload | string): void {
			const resolvedPayload = resolveStateCommandPayload(payload);
			void resolvedPayload.commandBus?.sendCommand(
				new SetTheStateForMultipleAggregates(
					[resolvedPayload.aggregateId],
					CommandProcessIdHandling.IGNORING,
					StatusEnum.SEEN
				)
			);
		},
		setTheAppointmentStateToUnseen(payload: SetTheAppointmentStatePayload | string): void {
			const resolvedPayload = resolveStateCommandPayload(payload);
			void resolvedPayload.commandBus?.sendCommand(
				new SetTheStateForMultipleAggregates(
					[resolvedPayload.aggregateId],
					CommandProcessIdHandling.IGNORING,
					StatusEnum.UNSEEN
				)
			);
		},
		setTimerFavorites(payload: SetTimerFavoritesPayload): void {
			this.timerFavorites = payload.timerFavorites;
		},
		timerFavoritesList(): Array<TimerFavorite> {
			return this.timerFavorites;
		},
		unsubscribeFetchAppointment(payload: UnsubscribeFetchAppointmentPayload): void {
			if (payload.queryStore === undefined) {
				return;
			}

			void payload.queryStore.unsubscribeQuery(new AppointmentQuery(payload.aggregateId));
		},
		updateRunningAppointmentTimerTitle(payload: UpdateRunningAppointmentTimerTitlePayload): void {
			const timerIndex = this.appointmentTimer.findIndex((item) => item.aggregateId === payload.aggregateId);
			if (timerIndex !== -1) {
				this.appointmentTimer[timerIndex].title = payload.title;
			}
		},
		updateTheAppointmentAccess(payload: UpdateTheAppointmentAccessPayload): void {
			const appointmentFromCollection = this.appointments.get(normalizeAggregateId(payload.aggregateId));
			if (appointmentFromCollection !== undefined) {
				appointmentFromCollection.access = payload.access;
				this.appointments.set(
					normalizeAggregateId(appointmentFromCollection.aggregateId),
					appointmentFromCollection
				);
			}
		},
		updateTheAppointmentAlert(payload: UpdateTheAppointmentAlertPayload): void {
			const appointmentFromCollection = this.appointments.get(normalizeAggregateId(payload.aggregateId));
			if (appointmentFromCollection !== undefined) {
				appointmentFromCollection.alert = payload.appointmentAlert;
				this.appointments.set(
					normalizeAggregateId(appointmentFromCollection.aggregateId),
					appointmentFromCollection
				);
			}
		},
		updateTheAppointmentDescription(payload: UpdateTheAppointmentDescriptionPayload): void {
			const appointmentFromCollection = this.appointments.get(normalizeAggregateId(payload.aggregateId));
			if (appointmentFromCollection !== undefined) {
				appointmentFromCollection.description = payload.description;
				this.appointments.set(
					normalizeAggregateId(appointmentFromCollection.aggregateId),
					appointmentFromCollection
				);
			}
		},
		updateTheAppointmentEndDateAndTime(payload: UpdateTheAppointmentEndDateAndTimePayload): void {
			const appointmentFromCollection = this.appointments.get(normalizeAggregateId(payload.aggregateId));
			if (appointmentFromCollection !== undefined) {
				appointmentFromCollection.endDate = payload.endDateAndTime;
				this.appointments.set(
					normalizeAggregateId(appointmentFromCollection.aggregateId),
					appointmentFromCollection
				);
			}
		},
		updateTheAppointmentIdoButton(payload: UpdateTheAppointmentIdoButtonPayload): void {
			const appointmentFromCollection = this.appointments.get(normalizeAggregateId(payload.aggregateId));
			if (appointmentFromCollection !== undefined) {
				appointmentFromCollection.idoButton = payload.idoButton;
				this.appointments.set(
					normalizeAggregateId(appointmentFromCollection.aggregateId),
					appointmentFromCollection
				);
			}
		},
		updateTheAppointmentLocation(payload: UpdateTheAppointmentLocationPayload): void {
			const appointmentFromCollection = this.appointments.get(normalizeAggregateId(payload.aggregateId));
			if (appointmentFromCollection !== undefined) {
				appointmentFromCollection.location = payload.location;
				this.appointments.set(
					normalizeAggregateId(appointmentFromCollection.aggregateId),
					appointmentFromCollection
				);
			}
		},
		updateTheAppointmentParticipantAcceptFlag(payload: UpdateTheAppointmentParticipantPayload): void {
			const appointmentFromCollection = this.appointments.get(normalizeAggregateId(payload.aggregateId));
			if (appointmentFromCollection !== undefined) {
				appointmentFromCollection.participantsOfAppointment[
					appointmentFromCollection.participantsOfAppointment.findIndex(
						(participant) => participant.userId === payload.participantOfTheAppointment.userId
					)
				] = {
					...payload.participantOfTheAppointment,
					accept: payload.participantOfTheAppointment.accept === 'false' ? 'true' : 'false',
				};
				this.appointments.set(
					normalizeAggregateId(appointmentFromCollection.aggregateId),
					appointmentFromCollection
				);
			}
		},
		updateTheAppointmentParticipantActiveFlag(payload: UpdateTheAppointmentParticipantPayload): void {
			const appointmentFromCollection = this.appointments.get(normalizeAggregateId(payload.aggregateId));
			if (appointmentFromCollection !== undefined) {
				appointmentFromCollection.participantsOfAppointment[
					appointmentFromCollection.participantsOfAppointment.findIndex(
						(participant) => participant.userId === payload.participantOfTheAppointment.userId
					)
				] = {
					...payload.participantOfTheAppointment,
					active: payload.participantOfTheAppointment.active === 'false' ? 'true' : 'false',
				};
				this.appointments.set(
					normalizeAggregateId(appointmentFromCollection.aggregateId),
					appointmentFromCollection
				);
			}
		},
		updateTheAppointmentParticipantAlertFlag(payload: UpdateTheAppointmentParticipantPayload): void {
			const appointmentFromCollection = this.appointments.get(normalizeAggregateId(payload.aggregateId));
			if (appointmentFromCollection !== undefined) {
				appointmentFromCollection.participantsOfAppointment[
					appointmentFromCollection.participantsOfAppointment.findIndex(
						(participant) => participant.userId === payload.participantOfTheAppointment.userId
					)
				] = {
					...payload.participantOfTheAppointment,
					alert: payload.participantOfTheAppointment.alert === 'false' ? 'true' : 'false',
				};
				this.appointments.set(
					normalizeAggregateId(appointmentFromCollection.aggregateId),
					appointmentFromCollection
				);
			}
		},
		updateTheAppointmentParticipantWriteFlag(payload: UpdateTheAppointmentParticipantPayload): void {
			const appointmentFromCollection = this.appointments.get(normalizeAggregateId(payload.aggregateId));
			if (appointmentFromCollection !== undefined) {
				appointmentFromCollection.participantsOfAppointment[
					appointmentFromCollection.participantsOfAppointment.findIndex(
						(participant) => participant.userId === payload.participantOfTheAppointment.userId
					)
				] = {
					...payload.participantOfTheAppointment,
					write: payload.participantOfTheAppointment.write === 'false' ? 'true' : 'false',
				};
				this.appointments.set(
					normalizeAggregateId(appointmentFromCollection.aggregateId),
					appointmentFromCollection
				);
			}
		},
		updateTheAppointmentParticipants(payload: UpdateTheAppointmentParticipantsPayload): void {
			const appointmentFromCollection = this.appointments.get(normalizeAggregateId(payload.aggregateId));
			if (appointmentFromCollection !== undefined) {
				appointmentFromCollection.participantsOfAppointment = payload.participantsOfAppointment;
				this.appointments.set(
					normalizeAggregateId(appointmentFromCollection.aggregateId),
					appointmentFromCollection
				);
			}
		},
		updateTheAppointmentRepetition(payload: UpdateTheAppointmentRepetitionPayload): void {
			const appointmentFromCollection = this.appointments.get(normalizeAggregateId(payload.aggregateId));
			if (appointmentFromCollection !== undefined) {
				appointmentFromCollection.calendarModelRepetition = payload.calendarModelRepetition;
				this.appointments.set(
					normalizeAggregateId(appointmentFromCollection.aggregateId),
					appointmentFromCollection
				);
			}
		},
		updateTheAppointmentStartDateAndTime(payload: UpdateTheAppointmentStartDateAndTimePayload): void {
			const appointmentFromCollection = this.appointments.get(normalizeAggregateId(payload.aggregateId));
			if (appointmentFromCollection !== undefined) {
				appointmentFromCollection.startDate = payload.startDateAndTime;
				this.appointments.set(
					normalizeAggregateId(appointmentFromCollection.aggregateId),
					appointmentFromCollection
				);
			}
		},
		updateTheAppointmentTimeMode(payload: UpdateTheAppointmentTimeModePayload): void {
			const appointmentFromCollection = this.appointments.get(normalizeAggregateId(payload.aggregateId));
			if (appointmentFromCollection !== undefined) {
				appointmentFromCollection.timeMode = payload.appointmentTimeMode;
				this.appointments.set(
					normalizeAggregateId(appointmentFromCollection.aggregateId),
					appointmentFromCollection
				);
			}
		},
		updateTheAppointmentTitle(payload: UpdateTheAppointmentTitlePayload): void {
			const appointmentFromCollection = this.appointments.get(normalizeAggregateId(payload.aggregateId));
			if (appointmentFromCollection !== undefined) {
				appointmentFromCollection.title = payload.title.replace(/\n/g, ' ');
				this.appointments.set(
					normalizeAggregateId(appointmentFromCollection.aggregateId),
					appointmentFromCollection
				);

				const timerIndex = this.appointmentTimer.findIndex((item) => item.aggregateId === payload.aggregateId);
				if (timerIndex !== -1) {
					this.appointmentTimer[timerIndex].title = payload.title.replace(/\n/g, ' ');
				}
			}
		},
		updateTheAppointmentType(payload: SetTheAppointmentTypePayload): void {
			const appointmentFromCollection = this.appointments.get(normalizeAggregateId(payload.aggregateId));
			if (appointmentFromCollection !== undefined) {
				appointmentFromCollection.appointmentType = payload.appointmentType;
				this.appointments.set(
					normalizeAggregateId(appointmentFromCollection.aggregateId),
					appointmentFromCollection
				);
			}
		},
		updateTheAppointmentTypeAction(payload: UpdateTheAppointmentTypePayload): void {
			if (payload.queryStore === undefined) {
				this.reset({
					aggregateId: payload.aggregateId,
				});
				return;
			}

			const appointmentFromCollection = this.appointments.get(normalizeAggregateId(payload.aggregateId));
			if (appointmentFromCollection === undefined) {
				this.reset({
					aggregateId: payload.aggregateId,
				});
				return;
			}

			const appointmentTypeId = Number.isInteger(appointmentFromCollection.appointmentType)
				? (appointmentFromCollection.appointmentType as number)
				: parseInt(appointmentFromCollection.appointmentType as string, 10);

			this.updateTheAppointmentType({
				aggregateId: payload.aggregateId,
				appointmentType: payload.appointmentType,
			});

			void payload.queryStore
				.query<AppointmentType>({
					callback: new ChangeAppointmentTypeOfSubmittedAppointmentIdCallBack(payload.aggregateId, {
						appointment: (aggregateId) => this.appointment(aggregateId),
						updateTheAppointmentAccess: (updatePayload) => this.updateTheAppointmentAccess(updatePayload),
						updateTheAppointmentAlert: (updatePayload) => this.updateTheAppointmentAlert(updatePayload),
						updateTheAppointmentIdoButton: (updatePayload) =>
							this.updateTheAppointmentIdoButton(updatePayload),
						updateTheAppointmentParticipants: (updatePayload) =>
							this.updateTheAppointmentParticipants(updatePayload),
						updateTheAppointmentTimeMode: (updatePayload) =>
							this.updateTheAppointmentTimeMode(updatePayload),
						updateTheAppointmentType: (updatePayload) => this.updateTheAppointmentType(updatePayload),
					}),
					createTime: new Date(),
					query: new ChangeAppointmentTypeOfSubmittedAppointmentId(
						appointmentTypeId,
						payload.appointmentType,
						appointmentFromCollection.appointmentId,
						appointmentFromCollection.calendarId
					),
					responseType: 'json',
					runType: AppointmentTypeRunType,
				})
				.then(() => {
					void payload.queryStore?.unsubscribeQuery(
						new ChangeAppointmentTypeOfSubmittedAppointmentId(
							appointmentTypeId,
							payload.appointmentType,
							appointmentFromCollection.appointmentId,
							appointmentFromCollection.calendarId
						)
					);
				});
		},
		updateTimerFavoritesOrder(payload: SetTimerFavoritesPayload): void {
			this.timerFavorites = payload.timerFavorites;
		},
	},
	state: (): AppointmentStoreState => createDefaultState(),
});
export * from './interfaces';
export * from './types';
